import { useCallback, useMemo } from 'react';

import { useIsClient } from 'hooks/useIsClient';
import { useLocalStorage } from 'hooks/useLocalStorage';

export const IDEALIST_NOTIFICATIONS_KEY = 'idealistNotifications';

export enum NotificationStatus {
  NEW = 'NEW',
  DISMISSED = 'DISMISSED',
}

// Local notifications displayed in the notifications dropdown
export enum DropdownNotification {
  OFFICE_HOURS = 'OFFICE_HOURS',
  ORG_ADMIN_USER_DROPDOWN_ATS_CTA = 'ORG_ADMIN_USER_DROPDOWN_ATS_CTA',
  OFFICE_HOURS_07_2024 = 'OFFICE_HOURS_07_2024',
  RESUME_WEBINAR = 'RESUME_WEBINAR',
  ES_SURVEY = 'ES_SURVEY',
}

// Local notifications displayed throughout the product,
// but never in the notifications dropdown
export enum ProductNotification {
  ORG_ADMIN_ATS_SURVEY_BANNER = 'ORG_ADMIN_ATS_SURVEY_BANNER',
  UPLOAD_RESUME = 'UPLOAD_RESUME',
  ORG_ADMIN_SURVEY_BANNER_2022 = 'ORG_ADMIN_SURVEY_BANNER_2022',
  SALARY_SURVEY_CTA = 'SALARY_SURVEY_CTA',
}

type NotificationType = DropdownNotification | ProductNotification;

export type LocalNotification = {
  status: NotificationStatus;
  viewed?: boolean;
  read?: boolean;
};

export type MappedLocalNotification = LocalNotification & {
  type: NotificationType;
};

export type LocalNotifications = {
  [k in NotificationType]?: LocalNotification;
};

/**
 *
 * A hook that returns localStorage notifications and a function to update them.
 * This will always return an empty object if localStorage is not set.
 */
export function useLocalNotifications() {
  const isClient = useIsClient();

  const [notifications, setNotifications] = useLocalStorage<LocalNotifications>(
    IDEALIST_NOTIFICATIONS_KEY,
  );

  // Transforms data stored as:
  // {
  //   "NOTIFICATION_TYPE": {
  //     status: "NEW"
  //   }
  // }
  //
  // to this format:
  // [
  //   {
  //     type: "NOTIFICATION_TYPE",
  //     status: "NEW"
  //   }
  // ]
  const localNotifications = useMemo(() => {
    // Default to an empty array if notifications are not ready yet.
    if (!isClient || notifications === null) return [];

    return Object.entries(notifications as LocalNotifications).map(
      ([key, value]) =>
        ({
          type: key as NotificationType,
          ...value,
        }) as MappedLocalNotification,
    );
  }, [notifications, isClient]);

  /**
   * A boolean that indicates if localStorage has been read at least once.
   */
  const isReady = useMemo(
    () => localNotifications !== null,
    [localNotifications],
  );

  const isSet = useCallback(
    (type: NotificationType) =>
      Boolean(
        localNotifications?.find((notification) => notification.type === type),
      ),
    [localNotifications],
  );

  const setLocalNotifications = useCallback(
    (mappedNotifications: MappedLocalNotification[]) => {
      if (!isClient) return;

      const transformed = mappedNotifications.reduce((acc, notification) => {
        const { type, ...rest } = notification;
        return {
          ...acc,
          ...{ [type]: rest },
        };
      }, {});

      setNotifications(transformed);
    },
    [isClient, setNotifications],
  );

  /**
   * A boolean that indicates if localStorage has been read at least once.
   *
   * __Note:__ this function can cause a race-condition if called multiple times in quick succession.
   *
   * @param name A notification key
   * @param value A partial object to update the notification with
   * @param initialState The initial state of the notifications to update against
   */
  const updateLocalNotification = useCallback(
    (
      name: NotificationType,
      value: Partial<MappedLocalNotification>,
      initialState?: LocalNotifications,
    ) => {
      if (!isClient) return;

      let currentNotification;
      if (initialState) {
        currentNotification = initialState ? initialState[name] : {};
      } else {
        currentNotification = notifications ? notifications[name] : {};
      }

      const newNotification = { ...currentNotification, ...value };

      const newState = {
        ...(initialState || notifications),
        [name]: newNotification,
      };

      setNotifications(newState);
      return newState;
    },
    [notifications, isClient, setNotifications],
  );

  return {
    localNotifications,
    isReady,
    isSet,
    setLocalNotifications,
    updateLocalNotification,
  };
}
